import Container = Phaser.GameObjects.Container;
import Scene = Phaser.Scene;
import {ImageButton} from "@/scenes.ui/image-button";
import {Keys} from "@/constants/key";
import {injectable} from "inversify";
import SoundUtils from "@/utils/sound-utils";
import CU from "@/utils/c-u";
import { Point } from "@/model/point";

@injectable()
export abstract class AbstractNavPanel extends Container {

    private navContainer!: Container;

    protected selectSound!: Phaser.Sound.BaseSound | undefined;

    private startIndex: number = 0;

    private desirableCreaturesAmount!: number;

    private elements!: any;
    protected elementsCount!: number;

    private maxDisplayedElements!: number;

    private panelByIndex!: Map<number, Container>;

    public getPosByIndex(index: number): Point {        
        return new Point(this.x + this.panelByIndex.get(index)!.x, this.y + this.panelByIndex.get(index)!.y);
    }

    constructor(scene: Scene, canvas: HTMLCanvasElement, x: number, y: number, elements: any, maxDisplayedElements: number,
                preconfigureCallback: (((ths: any) => unknown) | undefined) = undefined) {
        super(scene);
        this.panelByIndex = new Map<number, Phaser.GameObjects.Container>();
        if (preconfigureCallback) {
            preconfigureCallback(this);
        }

        this.x = x;
        this.y = y;

        this.setDepth(1000);

        this.selectSound = SoundUtils.addSound(scene, Keys.SOUND_SELECT);

        this.elements = elements;
        this.elementsCount = elements.length;

        this.maxDisplayedElements = maxDisplayedElements;

        this.navContainer = new Container(this.scene);

        if (this.elements.length <= this.maxDisplayedElements) {
            this.desirableCreaturesAmount = elements.length;
            for (let i = 0; i < this.elements.length; ++i) {
                this.addPanel(i, this.elements[i]);
            }
        } else {
            this.desirableCreaturesAmount = this.maxDisplayedElements;
            this.initNavPanel(this.elements, this.startIndex);
        }

        this.add(this.navContainer);
    }

    private initNavPanel(elements: any, startIndex: number) {

        this.navContainer.setVisible(false);
        this.navContainer.destroy();
        this.navContainer = new Container(this.scene);
        this.add(this.navContainer);

        while (startIndex < 0) {
            startIndex += elements.length;
        }

        for (let i = 0; i < this.maxDisplayedElements; i++) {
            let ind = (startIndex + i) % elements.length;
            this.addPanel(ind, elements[ind]);
        }

        const leftButton = new ImageButton(this.scene, -this.getButtonX(), this.getButtonY(), Keys.ATLAS_PROFILE, Keys.UI_ARROW_LEFT);
        const rightButton = new ImageButton(this.scene, this.getButtonX(), this.getButtonY(), Keys.ATLAS_PROFILE, Keys.UI_ARROW_RIGHT);

        this.add(leftButton);
        this.add(rightButton);

        leftButton.setCallback(() => {
            this.playSelectSound();
            this.shiftLeft();
            this.onLeftPressed();
        });

        rightButton.setCallback(() => {
            this.playSelectSound();
            this.shiftRight();
            this.onRightPressed();
        });
    }

    protected shiftLeft() {
        this.startIndex = this.startIndex - 1;
        while (this.startIndex < 0) {
            this.startIndex += this.elements.length;
        }
        this.initPanels();
    }

    protected shiftRight() {
        this.startIndex = this.startIndex + 1;
        while (this.startIndex >= this.elements.length) {
            this.startIndex -= this.elements.length;
        }
        this.initPanels();
    }

    private initPanels() {
        for (let p of this.panelByIndex.values()) {
            p.setVisible(false);
        }

        for (let i = 0; i < this.maxDisplayedElements; i++) {
            let ind = (this.startIndex + i) % this.elementsCount;
            this.addPanel(ind, this.elements[ind]);
        }
    }

    public getCurrentStartIndex(): any {
        return this.startIndex % this.elementsCount;
    }

    protected onLeftPressed() {}
    protected onRightPressed() {}

    private addPanel(index: number, element: any) {
        const cellWidth = this.calculateCellWidth(this.elementsCount);
        let cntr = this.navContainer.count("visible", true);
        let startX = cellWidth / 2 - this.desirableCreaturesAmount * cellWidth / 2;

        let x = startX + cntr * cellWidth;

        if (this.panelByIndex.has(index)) {
            let elementPanel = this.panelByIndex.get(index);
            elementPanel!.x = x * CU.S;
            elementPanel!.setVisible(true);
        } else {
            let elementPanel = this.makePanel(x, element);
            this.panelByIndex.set(index, elementPanel);
            this.navContainer.add(elementPanel);
        }
    }

    public playSelectSound() {
        SoundUtils.play(this.selectSound);
    }

    public getNavContainer() {
        return this.navContainer;
    }

    protected abstract getButtonX(): number;
    protected abstract getButtonY(): number;
    protected abstract makePanel(x: number, element: any): any;
    protected abstract calculateCellWidth(creaturesAmount: number): number;


}
